import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { Countdown } from './Countdown';
import { CHECKOUT_LINKS } from '../config/checkout';
import { trackInitiateCheckout, trackViewContent } from '../hooks/useMetaPixel';
export function Pricing() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const firedRef = useRef(false);
  useEffect(() => {
    if (!sectionRef.current || firedRef.current) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          trackViewContent();
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.3
    });
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  return <section ref={sectionRef} id="pricing" className="py-20 md:py-32 bg-white relative" data-id="element-125">
      <div className="max-w-5xl mx-auto px-4" data-id="element-126">
        {/* Urgency Header */}
        <div className="text-center mb-16" data-id="element-127">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-block bg-cream border border-sand rounded-2xl p-6 md:p-8 shadow-sm mb-8 max-w-2xl" data-id="element-128">
            <h3 className="text-sm font-bold tracking-widest text-ember uppercase mb-4 flex items-center justify-center gap-2" data-id="element-129">
              <span className="text-lg" data-id="element-130">🏷️</span> PRECIO DE LANZAMIENTO VIGENTE
            </h3>
            <p className="text-charcoal/80 text-sm md:text-base leading-relaxed mb-6" data-id="element-131">
              AirPack se encuentra actualmente en precio de lanzamiento. A
              medida que se agreguen nuevos contenidos y actualizaciones, el
              valor aumentará. Comprar hoy asegura acceso completo al precio
              actual.
            </p>
            <div className="scale-90 origin-center opacity-80" data-id="element-132">
              <Countdown data-id="element-133" />
            </div>
          </motion.div>
        </div>

        <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-12" data-id="element-134">
          ELEGÍ TU AIRPACK
        </h2>

        {/* Pricing Card — única oferta */}
        <div className="max-w-md mx-auto mb-20" data-id="element-135">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-cream border-2 border-ember rounded-2xl p-8 shadow-xl relative flex flex-col" data-id="element-136">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-ember text-white text-xs font-bold px-4 py-1.5 rounded-full tracking-widest uppercase shadow-sm whitespace-nowrap" data-id="element-137">
              ⭐ OFERTA DE LANZAMIENTO
            </div>

            <h3 className="text-2xl font-serif font-bold text-charcoal mb-2 flex items-center gap-2" data-id="element-138">
              <span data-id="element-139">📘</span> AIRPACK ESENCIAL
            </h3>
            <p className="text-sm font-medium text-charcoal/80 mb-6" data-id="element-140">
              6 Tomos + 4 Bonos
            </p>

            <div className="mb-8" data-id="element-141">
              <span className="line-through text-charcoal/40 text-lg font-medium block mb-1" data-id="element-142">
                $49.000
              </span>
              <div className="flex items-baseline gap-1" data-id="element-143">
                <span className="text-5xl font-serif font-bold text-ember" data-id="element-144">
                  $19.900
                </span>
                <span className="text-sm font-bold text-ember/80" data-id="element-145">ARS</span>
              </div>
            </div>

            <a href={CHECKOUT_LINKS.esencial} target="_blank" rel="noopener noreferrer" onClick={() => trackInitiateCheckout({
            plan: 'esencial',
            value: 19900
          })} className="w-full py-4 rounded-xl bg-ember hover:bg-ember-dark text-white font-bold transition-all hover:scale-[1.02] active:scale-[0.98] shadow-md mt-auto text-lg text-center block" data-id="element-146">
              → QUIERO MI AIRPACK ESENCIAL
            </a>
          </motion.div>
        </div>

        {/* Comparison Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto" data-id="element-147">
          <h3 className="text-2xl font-serif font-bold text-center mb-8" data-id="element-148">
            ¿Qué cambia con AirPack?
          </h3>

          <div className="bg-white rounded-2xl border border-sand shadow-sm overflow-hidden" data-id="element-149">
            <div className="overflow-x-auto scrollbar-hide" data-id="element-150">
              <table className="w-full text-left border-collapse min-w-[600px]" data-id="element-151">
                <thead data-id="element-152">
                  <tr className="bg-cream border-b border-sand" data-id="element-153">
                    <th className="p-4 md:p-6 font-bold text-charcoal/60 text-sm uppercase tracking-wider w-1/3" data-id="element-154">
                      Situación
                    </th>
                    <th className="p-4 md:p-6 font-bold text-charcoal/60 text-sm uppercase tracking-wider w-1/3" data-id="element-155">
                      <div className="flex items-center gap-2" data-id="element-156">
                        <X size={16} className="text-red-500" data-id="element-157" /> Sin Pack
                      </div>
                    </th>
                    <th className="p-4 md:p-6 font-bold text-ember text-sm uppercase tracking-wider w-1/3 bg-ember/5" data-id="element-158">
                      <div className="flex items-center gap-2" data-id="element-159">
                        <Check size={16} className="text-ember" data-id="element-160" /> Con Pack
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sand" data-id="element-161">
                  {[['No sabés qué cocinar esta semana', 'Delivery de nuevo', '365 días de menú resueltos'], ['Una receta salió mal', 'La repetís igual', 'Causa + solución en segundos'], ['Querés hacer repostería', 'Buscás en Google', '200 recetas de panes y tortas'], ['El gas y la luz suben', 'Seguís gastando igual', '40% menos de gasto'], ['Querés resolver la semana entera', 'Cocinás todos los días', 'Semana lista en 90 minutos']].map((row, i) => <tr key={i} className="hover:bg-cream/30 transition-colors" data-id="element-162">
                      <td className="p-4 md:p-6 text-sm md:text-base font-medium text-charcoal/80" data-id="element-163">
                        {row[0]}
                      </td>
                      <td className="p-4 md:p-6 text-sm md:text-base text-charcoal/60" data-id="element-164">
                        {row[1]}
                      </td>
                      <td className="p-4 md:p-6 text-sm md:text-base font-bold text-ember bg-ember/5" data-id="element-165">
                        {row[2]}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-center text-xs text-charcoal/40 mt-4 md:hidden italic" data-id="element-166">
            ← Deslizá para ver la tabla completa →
          </p>
        </motion.div>
      </div>
    </section>;
}